import React, { FC, useCallback, useState, useReducer, useEffect } from 'react';
import { invokeService, useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useParams } from 'react-router-dom';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { action } from 'typesafe-actions';
import { authorizationService, transactionService } from '../../../../../channels/secure-web/services';
import { TransactionDocument, TransactionDocumentData } from '../../domain/Transaction/entities/TransactionDocuments';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { CheckMandateResponseBody } from '../../../../authorizations/src/domain/Authorization/entities/CheckMandateResponseDTO';
import { useConsolidatedTransactions } from '../ConsolidatedTransactions/hooks';
import {
  ConsolidatedTransactionDetailsActionTypes,
  ConsolidatedTransactionDetailsProviderProps,
  TransactionDocumentsDataSetter,
} from './types';
import { ConsolidatedTransactionDetailsContext } from './ConsolidatedTransactionDetailsContext';
import { defaultConsolidatedTransactionDetailsProviderState } from './defaultState';
import { ConsolidatedTransactionDetailsReducer } from './ConsolidatedTransactionDetailsReducer';

const ConsolidatedTransactionDetailsProvider: FC<ConsolidatedTransactionDetailsProviderProps> = ({
  transactionList,
  children,
}) => {
  const [state, dispatch] = useReducer(
    ConsolidatedTransactionDetailsReducer,
    defaultConsolidatedTransactionDetailsProviderState,
  );
  const [transactionDetailsTimeStamp] = useState(new Date().toISOString());

  const { transactionNo } = useParams();

  const customerId = useSelectedCustomerId();

  // Recent Consolidated Transaction Details api call
  const shouldFetchTransactionDetails = !isEmpty(state.selectedConsolidatedTransaction?.orderId);
  const {
    data: consolidatedTransactionDetails,
    error: consolidatedTransactionDetailsError,
    isLoading: isLoadingConsolidatedTransactionDetails,
    mutate: consolidatedTransactionDetailsMutator,
  } = useInvokeSWRService(
    transactionService.GetConsolidatedTransactionDetails,
    {
      orderId: state.selectedConsolidatedTransaction?.orderId,
      accountNumber: state.selectedConsolidatedTransaction?.accountNumber,
      meta: {
        timestamp: state.selectedConsolidatedTransaction?.timestamp ?? transactionDetailsTimeStamp,
      },
    },
    shouldFetchTransactionDetails,
  );

  const shouldCheckAuthorization =
    Boolean(state.selectedConsolidatedTransaction) &&
    state.selectedConsolidatedTransaction !== null &&
    Boolean(consolidatedTransactionDetails);

  // Check Authorization
  const {
    data: checkAuthorizationData,
    isLoading: isLoadingCheckAuthorization,
    error: checkAuthorizationError,
    mutate: checkAuthorizationMutation,
  } = useInvokeSWRService<CheckMandateResponseBody>(
    authorizationService.CheckMandate,
    {
      accountNumber: consolidatedTransactionDetails?.fromAccount,
      uniqueId: Number(consolidatedTransactionDetails?.transactionId),
      amount: Number(consolidatedTransactionDetails?.amount),
      meta: {
        timestamp: state.selectedConsolidatedTransaction?.timestamp ?? transactionDetailsTimeStamp,
      },
    },
    shouldCheckAuthorization,
  );

  // this method can be used also for AccountExtraitTransactions to fetch any documents in AccountExtraitTransaction details
  const getTransactionDocuments = useCallback(
    async (orderId: number, dataSetter: TransactionDocumentsDataSetter) => {
      const { data, error, isLoading } = await invokeService<Nullable<TransactionDocument[]>>(
        () => transactionService.GetTransactionDocuments({ customerId: Number(customerId), orderId }),
        Boolean(orderId),
      );

      dataSetter({ documents: data, error, isLoading });
    },
    [customerId],
  );

  // this method can be used also for AccountExtraitTransactions to fetch any document data in AccountExtraitTransaction details
  const getSingleFileDocumentData = useCallback(
    async (fileSn: number, referenceNumber: string): Promise<any> => {
      const { data } = await invokeService<Nullable<TransactionDocumentData>>(
        () =>
          transactionService.GetTransactionDocumentData({
            customerId: Number(customerId),
            fileSn,
            referenceNumber,
          }),
        Boolean(fileSn && referenceNumber),
      );
      return data;
    },
    [customerId],
  );

  const onSelectConsolidatedTransaction = useCallback(
    (transactionNo?: string) => {
      if (transactionNo && transactionList) {
        const selectedTransaction = transactionList.find(transaction => transaction.transactionNo === transactionNo);
        dispatch(
          action(ConsolidatedTransactionDetailsActionTypes.setSelectedConsolidatedTransaction, {
            ...selectedTransaction,
            timestamp: new Date().toISOString(),
          }),
        );
      } else {
        dispatch(action(ConsolidatedTransactionDetailsActionTypes.setSelectedConsolidatedTransaction, null));
      }
    },
    [transactionList],
  );

  const resetSelectedConsolidatedTransaction = useCallback(() => {
    dispatch(action(ConsolidatedTransactionDetailsActionTypes.setSelectedConsolidatedTransaction, null));
  }, []);

  const refetchConsolidatedTransactionDetails = useCallback(() => {
    consolidatedTransactionDetailsMutator && consolidatedTransactionDetailsMutator();
  }, [consolidatedTransactionDetailsMutator]);

  const onCheckAuthorizationMutation = useCallback(() => {
    checkAuthorizationMutation && checkAuthorizationMutation();
  }, [checkAuthorizationMutation]);

  useEffect(() => {
    if (
      transactionNo &&
      isEmpty(state.selectedConsolidatedTransaction) &&
      transactionList &&
      !isEmpty(transactionList)
    ) {
      onSelectConsolidatedTransaction(transactionNo);
    }
  }, [transactionNo, onSelectConsolidatedTransaction, state.selectedConsolidatedTransaction, transactionList]);

  const values = {
    ...state,
    checkAuthorizationData,
    consolidatedTransactionDetails,
    getTransactionDocuments,
    getSingleFileDocumentData,
    onSelectConsolidatedTransaction,
    resetSelectedConsolidatedTransaction,
    refetchConsolidatedTransactionDetails,
    onCheckAuthorizationMutation,
    loadingState: {
      isLoadingConsolidatedTransactionDetails,
      isLoadingCheckAuthorization,
    },
    errorState: {
      consolidatedTransactionDetailsError,
      checkAuthorizationError,
    },
  };

  return (
    <ConsolidatedTransactionDetailsContext.Provider value={values}>
      {children}
    </ConsolidatedTransactionDetailsContext.Provider>
  );
};

const ConsolidatedTransactionDetailsWrapper: FC<{}> = ({ children }) => {
  const transactionList = useConsolidatedTransactions();
  return (
    <ConsolidatedTransactionDetailsProvider transactionList={transactionList}>
      {children}
    </ConsolidatedTransactionDetailsProvider>
  );
};

export { ConsolidatedTransactionDetailsProvider, ConsolidatedTransactionDetailsWrapper };
